import React, { useEffect, useMemo, useState } from "react";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  listAll,
  ref,
} from "firebase/storage";
import app_connection from "../../firebase.js";
import "./BannerManager.css";

const BANNER_FOLDER = "banner-memes";

function getFirebaseMessage(action, error) {
  const errorCode = error?.code ? ` (${error.code})` : "";

  return `${action} failed${errorCode}. Check Firebase Storage rules for the banner-memes folder.`;
}

function BannerManager() {
  const [images, setImages] = useState([]);
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [message, setMessage] = useState("");

  const storage = useMemo(() => getStorage(app_connection), []);
  const selectedCount = selectedPaths.length;
  const allSelected = images.length > 0 && selectedCount === images.length;

  const loadImages = async () => {
    setIsLoading(true);
    setMessage("");

    try {
      const folderRef = ref(storage, BANNER_FOLDER);
      const imageRefs = await listAll(folderRef);
      const loadedImages = await Promise.all(
        imageRefs.items.map(async (imageRef) => ({
          name: imageRef.name,
          path: imageRef.fullPath,
          url: await getDownloadURL(imageRef),
        }))
      );

      loadedImages.sort((a, b) => a.name.localeCompare(b.name));
      setImages(loadedImages);
      setSelectedPaths((currentPaths) =>
        currentPaths.filter((path) =>
          loadedImages.some((image) => image.path === path)
        )
      );
    } catch (error) {
      console.error("Banner manager image load failed", error);
      setMessage(getFirebaseMessage("Loading images", error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
  }, []);

  const toggleImage = (path) => {
    setSelectedPaths((currentPaths) =>
      currentPaths.includes(path)
        ? currentPaths.filter((currentPath) => currentPath !== path)
        : [...currentPaths, path]
    );
  };

  const toggleAll = () => {
    setSelectedPaths(allSelected ? [] : images.map((image) => image.path));
  };

  const deleteSelected = async () => {
    if (!selectedCount) {
      setMessage("Select at least one image to delete.");
      return;
    }

    setIsDeleting(true);
    setMessage("");

    try {
      await Promise.all(
        selectedPaths.map((path) => deleteObject(ref(storage, path)))
      );

      setMessage(`${selectedCount} image${selectedCount === 1 ? "" : "s"} deleted.`);
      setSelectedPaths([]);
      await loadImages();
    } catch (error) {
      console.error("Banner manager delete failed", error);
      setMessage(getFirebaseMessage("Delete", error));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <main className="BannerManager">
      <section className="BannerManager-toolbar">
        <button type="button" onClick={loadImages} disabled={isLoading || isDeleting}>
          Refresh
        </button>
        <button type="button" onClick={toggleAll} disabled={!images.length || isDeleting}>
          {allSelected ? "Clear Selection" : "Select All"}
        </button>
        <button
          className="BannerManager-delete"
          type="button"
          onClick={deleteSelected}
          disabled={!selectedCount || isDeleting}
        >
          {isDeleting ? "Deleting..." : `Delete Selected (${selectedCount})`}
        </button>
      </section>

      {message && <p className="BannerManager-message">{message}</p>}

      <section className="BannerManager-gallery" aria-label="Uploaded banner images">
        {isLoading && <p className="BannerManager-empty">Loading images...</p>}
        {!isLoading && !images.length && (
          <p className="BannerManager-empty">No banner images are in the bucket.</p>
        )}
        {images.map((image) => (
          <label className="BannerManager-card" key={image.path}>
            <input
              type="checkbox"
              checked={selectedPaths.includes(image.path)}
              onChange={() => toggleImage(image.path)}
              disabled={isDeleting}
            />
            <img src={image.url} alt={image.name} />
            <span title={image.name}>{image.name}</span>
          </label>
        ))}
      </section>
    </main>
  );
}

export default BannerManager;
